/**
 * Unified search: same behavior as the shell search — /api/search preview, submit → /search?q=
 * Use controlled mode on Explore/Questions to also drive local filtering.
 */
import { useDeferredValue, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiJson } from '../api';
import './GlobalSearch.css';

export type SearchApiResult = {
  modules: Array<{ slug: string; title: string; summary: string | null; topic_family?: string | null; matchIn?: string }>;
  journeys: Array<{ slug: string; title: string; description?: string | null; step_count?: number }>;
  topics: Array<{ slug: string; title: string; family?: string | null }>;
  query: string;
};

type Props = {
  variant: 'header' | 'page';
  /** Controlled (Explore/Questions) — filters list while typing */
  value?: string;
  onValueChange?: (v: string) => void;
  placeholder?: string;
  className?: string;
  /** id for label association */
  inputId?: string;
};

export function SearchBarCore(props: Props) {
  const { variant, value: controlled, onValueChange, placeholder, className, inputId } = props;
  const [internal, setInternal] = useState('');
  const isControlled = controlled !== undefined;
  const q = isControlled ? controlled : internal;
  const setQ = (v: string) => {
    if (onValueChange) onValueChange(v);
    if (!isControlled) setInternal(v);
  };

  const deferred = useDeferredValue(q);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<SearchApiResult | null>(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (deferred.trim().length < 2) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await apiJson<SearchApiResult>(`/api/search?q=${encodeURIComponent(deferred)}`);
        if (!cancelled) setData(res);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deferred]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    setOpen(false);
    try {
      await apiJson('/api/events', {
        method: 'POST',
        body: JSON.stringify({
          eventType: 'search_submitted',
          entityType: 'search',
          entityId: q.trim().slice(0, 64),
          payload: { q: q.trim(), surface: variant },
        }),
      });
    } catch {
      /* ignore */
    }
    nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const hasResults =
    data &&
    (data.modules.length > 0 || data.journeys.length > 0 || (data.topics && data.topics.length > 0));

  const wrapClass =
    variant === 'page'
      ? `wi-global-search wi-unified-search--page ${className ?? ''}`
      : `wi-global-search ${className ?? ''}`;

  return (
    <div className={wrapClass}>
      <form onSubmit={submit} className="wi-global-search-form">
        <input
          id={inputId ?? 'wi-unified-search'}
          type="search"
          className={`wi-global-search-input${variant === 'page' ? ' wi-global-search-input--page' : ''}`}
          placeholder={placeholder ?? 'Search modules, journeys…'}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          aria-label="Search"
        />
        <button type="submit" className="wi-global-search-submit">
          Search
        </button>
      </form>
      {open && q.trim().length >= 2 ? (
        <div className="wi-global-search-dropdown" role="listbox">
          {loading ? <p className="wi-global-search-muted">Searching…</p> : null}
          {!loading && data && !hasResults ? (
            <p className="wi-global-search-muted">No matches — try different wording.</p>
          ) : null}
          {data?.modules.length ? (
            <div className="wi-global-search-group">
              <p className="wi-global-search-group-label">Modules</p>
              <ul>
                {data.modules.slice(0, 6).map((m) => (
                  <li key={m.slug}>
                    <Link to={`/m/${m.slug}`} onClick={() => setOpen(false)}>
                      {m.title}
                    </Link>
                    {m.summary ? <span className="wi-global-search-meta">{m.summary.slice(0, 90)}…</span> : null}
                    {m.matchIn ? (
                      <span className="wi-global-search-match">{matchLabel(m.matchIn)}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {data?.journeys.length ? (
            <div className="wi-global-search-group">
              <p className="wi-global-search-group-label">Journeys</p>
              <ul>
                {data.journeys.slice(0, 4).map((j) => (
                  <li key={j.slug}>
                    <Link to={`/journeys/${j.slug}`} onClick={() => setOpen(false)}>
                      {j.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <Link
            className="wi-global-search-all"
            to={`/search?q=${encodeURIComponent(q.trim())}`}
            onClick={() => setOpen(false)}
          >
            View all results →
          </Link>
        </div>
      ) : null}
    </div>
  );
}

function matchLabel(m: string): string {
  if (m === 'title') return 'Matched in title';
  if (m === 'summary') return 'Matched in summary';
  if (m === 'both') return 'Title & summary';
  return '';
}
